<template>
    <div class="main-content">
        <!-- Toast de Notificações -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">{{ alert.message }}</div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <!-- Header e Breadcrumbs -->
        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">CRM (Atendimentos)</li>
                </ol>
            </nav>
        </header>

        <!-- Cards de Estatísticas -->
        <div class="row g-3 mb-4">
            <div class="col-xl-3 col-md-6">
                <div class="card border-0 shadow-sm h-100 bg-white">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-shrink-0 bg-pmg-light p-3 rounded-3">
                                <i class="bi bi-headset fs-4 text-primary"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="text-muted small mb-1">Total de Tickets</h6>
                                <h4 class="mb-0 fw-bold">{{ stats.total }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6">
                <div class="card border-0 shadow-sm h-100 bg-white">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-shrink-0 bg-warning-light p-3 rounded-3">
                                <i class="bi bi-envelope-open-fill fs-4 text-warning"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="text-muted small mb-1">Abertos / Andamento</h6>
                                <h4 class="mb-0 fw-bold">{{ stats.open }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6">
                <div class="card border-0 shadow-sm h-100 bg-white">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-shrink-0 bg-danger-light p-3 rounded-3">
                                <i class="bi bi-alarm-fill fs-4 text-danger"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="text-muted small mb-1">Retornos Atrasados</h6>
                                <h4 class="mb-0 fw-bold">{{ stats.delayed }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6">
                <div class="card border-0 shadow-sm h-100 bg-white">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-shrink-0 bg-success-light p-3 rounded-3">
                                <i class="bi bi-check-circle-fill fs-4 text-success"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="text-muted small mb-1">Concluídos</h6>
                                <h4 class="mb-0 fw-bold">{{ stats.completed }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Barra de Ferramentas e Tabela -->
        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div
                class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <div class="flex-shrink-0">
                    <h4 class="mb-0 fw-bold">Atendimentos CRM</h4>
                    <span class="text-muted fs-7 text-nowrap">Exibindo {{ tickets.length }} de {{ totalItems }}</span>
                </div>

                <div
                    class="d-flex flex-wrap gap-2 align-items-center w-100 w-md-auto justify-content-start justify-content-md-end">
                    <div class="dropdown me-2 pe-3 border-end">
                        <button class="btn btn-pmg-light dropdown-toggle w-100" type="button" data-bs-toggle="dropdown"
                            aria-expanded="false">
                            <i class="bi bi-printer me-1"></i> Relatórios / Ferramentas
                        </button>
                        <ul class="dropdown-menu shadow border-0 py-2">
                            <li>
                                <h6 class="dropdown-header text-uppercase small fw-bold opacity-75">Exportar Dados</h6>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="openReportModal('pdf')">
                                    <i class="bi bi-file-earmark-pdf text-danger me-2"></i> Relatório Geral (PDF)
                                </a>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="openReportModal('xlsx')">
                                    <i class="bi bi-file-earmark-excel text-success me-2"></i> Planilha Completa (Excel)
                                </a>
                            </li>
                        </ul>
                    </div>
                    <button class="btn btn-pmg-light" @click="fetchTickets(1)" :disabled="loading"
                        title="Atualizar tela">
                        <i class="bi bi-arrow-clockwise" :class="{ 'spin-animation': loading }"></i>
                    </button>
                    <button class="btn btn-pmg-light flex-fill flex-md-grow-0" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>
                    <button class="btn btn-pmg-primary" @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Novo Atendimento
                    </button>
                </div>
            </div>

            <!-- Filtros Avançados -->
            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-3">
                            <input v-model="filter.protocol" type="text" class="form-control form-control-pmg"
                                placeholder="Protocolo (Ticket)">
                        </div>
                        <div class="col-md-3">
                            <select v-model="filter.status" class="form-select form-control-pmg">
                                <option value="">Todos os Status</option>
                                <option value="Aberto">Aberto</option>
                                <option value="Em Andamento">Em Andamento</option>
                                <option value="Concluído">Concluído</option>
                            </select>
                        </div>
                        <div class="col-md-4">
                            <select v-model="filter.reason_id" class="form-select form-control-pmg">
                                <option value="">Todos os Motivos</option>
                                <option v-for="r in reasons" :key="r.id" :value="r.id">{{ r.title }}</option>
                            </select>
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Buscar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Botões de Alternância (Lista / Kanban) -->
            <div class="d-flex justify-content-end mb-3 gap-2">
                <div class="btn-group shadow-sm" role="group">
                    <button type="button" class="btn btn-sm fw-medium"
                        :class="viewMode === 'list' ? 'btn-pmg-primary' : 'btn-pmg-light'" @click="viewMode = 'list'">
                        <i class="bi bi-list-task me-1"></i> Lista
                    </button>
                    <button type="button" class="btn btn-sm fw-medium"
                        :class="viewMode === 'kanban' ? 'btn-pmg-primary' : 'btn-pmg-light'"
                        @click="viewMode = 'kanban'">
                        <i class="bi bi-kanban me-1"></i> Kanban
                    </button>
                </div>
            </div>

            <!-- Quadro Kanban -->
            <div v-if="viewMode === 'kanban'" class="row g-3 mb-4">
                <div v-for="colStatus in kanbanColumns" :key="colStatus" class="col-lg-3 col-md-6">
                    <div class="bg-light rounded-4 p-3 h-100 border" @dragover.prevent @dragenter.prevent
                        @drop="onDrop($event, colStatus)">

                        <h6 class="text-uppercase fw-bold text-secondary mb-3 d-flex justify-content-between align-items-center"
                            style="letter-spacing: 1px; font-size: 0.8rem;">
                            {{ colStatus }}
                            <span class="badge bg-secondary-subtle text-secondary rounded-pill fs-7">
                                {{ groupedTickets[colStatus]?.length || 0 }}
                            </span>
                        </h6>

                        <div class="kanban-drop-zone kanban-scroll-area">
                            <div v-for="ticket in groupedTickets[colStatus]" :key="ticket.id" draggable="true"
                                @dragstart="onDragStart($event, ticket)"
                                class="card shadow-sm mb-3 cursor-grab kanban-card"
                                :class="getCardColor(ticket.status)">

                                <div class="card-body p-3">
                                    <div class="d-flex justify-content-between align-items-start mb-2">
                                        <span class="badge bg-primary-subtle text-primary fw-bold">{{ ticket.protocol
                                            }}</span>
                                        <button class="btn btn-sm btn-link text-muted p-0"
                                            @click="openModal('edit', ticket)" title="Editar">
                                            <i class="bi bi-pencil"></i>
                                        </button>
                                    </div>

                                    <div class="fw-bold text-dark fs-6 mb-1 line-clamp-1">
                                        {{ ticket.crmable?.name || 'Cliente não informado' }}
                                    </div>

                                    <p class="small text-muted mb-2 line-clamp-2" style="font-size: 0.8rem;">
                                        <i class="bi bi-tag-fill me-1 opacity-50"></i>
                                        {{ ticket.reason?.title || 'Sem motivo' }}
                                    </p>

                                    <!-- NOVO: Usuário Criador -->
                                    <div class="mb-3 text-muted" style="font-size: 0.75rem;">
                                        <i class="bi bi-person-circle me-1 opacity-50"></i>
                                        Criado por: <span class="fw-medium text-dark">{{ ticket.user?.name || 'Sistema'
                                            }}</span>
                                    </div>

                                    <div class="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <span class="tiny fw-medium"
                                            :class="isDelayed(ticket.return_date) ? 'text-danger' : 'text-secondary'">
                                            <i class="bi bi-calendar me-1"></i> {{ ticket.return_date ?
                                                formatDateBR(ticket.return_date) : 'Sem retorno' }}
                                        </span>

                                        <button class="btn btn-sm text-danger p-0 border-0 bg-transparent"
                                            @click="confirmDelete(ticket)" title="Excluir">
                                            <i class="bi bi-trash3"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!groupedTickets[colStatus] || groupedTickets[colStatus].length === 0"
                                class="d-flex align-items-center justify-content-center h-100 border-2 border-dashed rounded-3 p-4 opacity-50">
                                <span class="small text-muted fw-medium">Arraste cards para cá</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabela Principal -->
            <div v-if="viewMode === 'list'" class="table-responsive" style="overflow-x: auto; width: 100%;">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 15%">Protocolo</th>
                            <th style="width: 25%">Cliente / Fornecedor</th>
                            <th style="width: 20%">Assunto / Motivo</th>
                            <th style="width: 15%" class="text-center">Data Retorno</th>
                            <th style="width: 15%" class="text-center">Status</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>
                    <tbody v-if="tickets.length > 0">
                        <tr v-for="item in tickets" :key="item.id">
                            <td>
                                <div class="fw-bold text-dark">{{ item.protocol }}</div>
                                <div class="tiny text-muted mb-1">
                                    <i class="bi bi-clock me-1"></i> {{ formatDate(item.created_at) }}
                                </div>
                                <!-- NOVO: Usuário Criador -->
                                <div class="tiny text-secondary">
                                    <i class="bi bi-person-fill me-1"></i> {{ item.user?.name || 'Sistema' }}
                                </div>
                            </td>
                            <td>
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge bg-secondary-subtle text-secondary tiny text-uppercase">
                                        {{ getEntityLabel(item.crmable_type) }}
                                    </span>
                                    <span class="fw-medium text-dark">{{ item.crmable?.name || 'Desconhecido' }}</span>
                                </div>
                            </td>
                            <td>
                                <div class="text-truncate" style="max-width: 200px;" :title="item.reason?.title">
                                    {{ item.reason?.title }}
                                </div>
                                <div v-if="item.itemable" class="tiny text-primary mt-1">
                                    <i class="bi bi-box me-1"></i> {{ item.itemable.name }}
                                </div>
                            </td>
                            <td class="text-center">
                                <span v-if="item.return_date" class="small fw-medium"
                                    :class="isDelayed(item.return_date) ? 'text-danger' : 'text-dark'">
                                    <i class="bi bi-calendar-event me-1"></i> {{ formatShortDate(item.return_date) }}
                                </span>
                                <span v-else class="small text-muted">-</span>
                            </td>
                            <td class="text-center">
                                <span :class="getStatusBadgeClass(item.status)" class="rounded-pill px-3 small fw-bold">
                                    {{ item.status }}
                                </span>
                            </td>
                            <td class="text-end text-nowrap">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        aria-expanded="false">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>

                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 py-2">
                                        <li>
                                            <a class="dropdown-item py-2 d-flex align-items-center" href="#"
                                                @click.prevent="openModal('edit', item)">
                                                <i class="bi bi-pencil me-2 text-secondary fs-6"></i> Editar
                                            </a>
                                        </li>
                                        <li>
                                            <hr class="dropdown-divider opacity-10 my-1">
                                        </li>
                                        <li>
                                            <a class="dropdown-item py-2 d-flex align-items-center text-danger" href="#"
                                                @click.prevent="confirmDelete(item)">
                                                <i class="bi bi-trash3 me-2 fs-6"></i> Remover
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="6" class="text-center py-5 text-muted">
                                <i class="bi bi-inbox fs-1 mb-2 opacity-50"></i><br>
                                <span>Nenhum atendimento encontrado.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMore" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <span>{{ loading ? 'Carregando...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <!-- Modal de Criação / Edição do CRM -->
        <div class="modal fade" id="modalCrmTicket" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-2">
                        <h4 class="fw-bold m-0 text-dark">{{ isEditing ? 'Editar Atendimento' : 'Novo Atendimento CRM'
                            }}</h4>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>

                    <div class="modal-body px-4 py-4">
                        <!-- Navegação das Abas (Aparece apenas na Edição) -->
                        <ul class="nav nav-tabs mb-4 border-bottom-0" v-if="isEditing">
                            <li class="nav-item">
                                <button class="nav-link fw-bold" :class="{ 'active': activeTab === 'details' }"
                                    @click="activeTab = 'details'" type="button">
                                    <i class="bi bi-card-list me-1"></i> Detalhes do Atendimento
                                </button>
                            </li>
                            <li class="nav-item">
                                <button class="nav-link fw-bold" :class="{ 'active': activeTab === 'notes' }"
                                    @click="activeTab = 'notes'" type="button">
                                    <i class="bi bi-chat-left-text me-1"></i> Histórico Interno
                                    <span class="badge bg-secondary ms-1 rounded-pill" style="font-size: 0.7rem;">{{
                                        currentTicketNotes.length }}</span>
                                </button>
                            </li>
                        </ul>

                        <!-- Conteúdo das Abas -->
                        <div class="tab-content">

                            <!-- ABA 1: DETALHES (Sempre aparece na criação, ou quando selecionada na edição) -->
                            <div class="tab-pane fade"
                                :class="{ 'show active': activeTab === 'details' || !isEditing }">
                                <div class="row g-4">
                                    <!-- Entidade Polimórfica (Cliente/Fornecedor) com Autocomplete Dinâmico -->
                                    <div class="col-md-4">
                                        <label class="form-label fw-bold text-secondary small mb-1">Vincular a *</label>
                                        <select v-model="form.crmable_type" class="form-select form-control-pmg"
                                            @change="onCrmableTypeChange">
                                            <option value="App\Models\Client">Cliente</option>
                                            <option value="App\Models\Supplier">Fornecedor</option>
                                        </select>
                                    </div>

                                    <div class="col-md-8 position-relative">
                                        <label class="form-label fw-bold text-secondary small mb-1">Buscar Registro
                                            (Cliente/Fornecedor) *</label>
                                        <div class="position-relative">
                                            <i
                                                class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            <input type="text" v-model="contactSearchQuery" @input="onContactInput"
                                                @focus="showContactDropdown = true" @blur="hideContactDropdown"
                                                class="form-control form-control-pmg ps-5"
                                                placeholder="Digite o nome para buscar...">
                                        </div>

                                        <div v-if="showContactDropdown && contactSearchResults.length > 0"
                                            class="autocomplete-dropdown shadow-lg rounded-3 border bg-white position-absolute w-100 mt-1"
                                            style="z-index: 1060;">
                                            <ul class="list-unstyled mb-0">
                                                <li v-for="contact in contactSearchResults" :key="contact.id"
                                                    @mousedown="selectContact(contact)"
                                                    class="p-3 autocomplete-item border-bottom cursor-pointer">
                                                    <div class="fw-bold text-dark">{{ contact.name }}</div>
                                                    <div class="small text-muted">{{ contact.document || 'Sem Documento'
                                                        }}</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <hr class="opacity-10 my-1">
                                    </div>

                                    <!-- Motivo -->
                                    <div class="col-md-12">
                                        <label class="form-label fw-bold text-secondary small mb-1">Motivo / Assunto
                                            *</label>
                                        <select v-model="form.reason_id" class="form-select form-control-pmg">
                                            <option value="">Selecione...</option>
                                            <option v-for="r in reasons" :key="r.id" :value="r.id">{{ r.title }}
                                            </option>
                                            <option value="NEW">+ Cadastrar Novo Motivo / Assunto</option>
                                        </select>
                                    </div>

                                    <div class="col-md-12" v-if="form.reason_id === 'NEW'">
                                        <label class="form-label fw-bold text-primary small mb-1">Qual o novo motivo / assunto?
                                            *</label>
                                        <input v-model="form.new_reason" type="text"
                                            class="form-control form-control-pmg border-primary"
                                            placeholder="Ex: Dúvida Contratual">
                                    </div>

                                    <!-- Produto ou Serviço Relacionado -->
                                    <div class="col-12">
                                        <label class="form-label fw-bold text-secondary small mb-1">Produto ou Serviço
                                            Relacionado</label>
                                        <div class="row g-2">
                                            <div class="col-md-4">
                                                <select v-model="form.itemable_type"
                                                    class="form-select form-control-pmg" @change="onItemableTypeChange">
                                                    <option value="">Nenhum / Sem item</option>
                                                    <option value="App\Models\Product">Produto</option>
                                                    <option value="App\Models\Service">Serviço</option>
                                                </select>
                                            </div>

                                            <div class="col-md-8 position-relative">
                                                <input type="text" v-model="itemSearchQuery" @input="onItemInput"
                                                    @focus="showItemDropdown = true" @blur="hideItemDropdown"
                                                    class="form-control form-control-pmg"
                                                    :disabled="!form.itemable_type"
                                                    :placeholder="form.itemable_type ? 'Digite para buscar o item...' : 'Selecione o tipo ao lado primeiro...'">

                                                <div v-if="showItemDropdown && itemSearchResults.length > 0"
                                                    class="autocomplete-dropdown shadow-lg rounded-3 border bg-white position-absolute w-100 mt-1"
                                                    style="z-index: 1060; max-height: 250px; overflow-y: auto;">
                                                    <ul class="list-unstyled mb-0">
                                                        <li v-for="it in itemSearchResults" :key="it.id"
                                                            @mousedown="selectItem(it)"
                                                            class="p-3 autocomplete-item border-bottom cursor-pointer">
                                                            <div class="fw-bold text-dark">{{ it.name ?? it.description }}</div>
                                                            <div class="small text-muted">Preço: R$ {{
                                                                formatMoney(it.sale_price || it.price || 0) }}</div>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Dados do Atendimento -->
                                    <div class="col-md-6">
                                        <label class="form-label fw-bold text-secondary small mb-1">Status do
                                            Atendimento *</label>
                                        <select v-model="form.status" class="form-select form-control-pmg">
                                            <option value="Aberto">Aberto</option>
                                            <option value="Em Andamento">Em Andamento</option>
                                            <option value="Aguardando Cliente">Aguardando Cliente</option>
                                            <option value="Concluído">Concluído</option>
                                        </select>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label fw-bold text-secondary small mb-1">Data de Retorno
                                            Prevista</label>
                                        <input v-model="form.return_date" type="date"
                                            class="form-control form-control-pmg">
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label fw-bold text-secondary small mb-1">Histórico /
                                            Descrição Inicial *</label>
                                        <textarea v-model="form.description" class="form-control form-control-pmg"
                                            rows="3"
                                            placeholder="Descreva os detalhes iniciais da interação..."></textarea>
                                    </div>
                                </div>
                            </div>

                            <!-- ABA 2: NOTAS (Só existe se for edição) -->
                            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'notes' }"
                                v-if="isEditing">

                                <div
                                    class="alert alert-light border shadow-sm rounded-3 mb-4 d-flex align-items-center">
                                    <i class="bi bi-info-circle text-primary fs-4 me-3"></i>
                                    <div>
                                        <h6 class="fw-bold mb-1">Histórico de Interações</h6>
                                        <p class="mb-0 small text-muted">Adicione anotações internas para manter o time
                                            alinhado sobre o andamento deste chamado.</p>
                                    </div>
                                </div>

                                <!-- Caixa de nova nota -->
                                <div class="mb-4 bg-light p-3 rounded-4 border">
                                    <label class="form-label fw-bold text-dark small mb-2">Registrar nova
                                        interação</label>
                                    <textarea v-model="newNoteContent" class="form-control form-control-pmg bg-white"
                                        rows="2"
                                        placeholder="O que aconteceu agora? Descreva a interação, cobrança ou andamento..."></textarea>
                                    <div class="d-flex justify-content-end mt-3">
                                        <button type="button" class="btn btn-sm btn-pmg-primary px-3"
                                            style="height: 38px;" @click="saveNote"
                                            :disabled="!newNoteContent.trim() || isSavingNote">
                                            <span v-if="isSavingNote"
                                                class="spinner-border spinner-border-sm me-1"></span>
                                            <i v-else class="bi bi-send me-1"></i> {{ isSavingNote ? 'Salvando...' :
                                                'Registrar Nota' }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Timeline de Notas -->
                                <h6 class="fw-bold text-secondary mb-3 ms-1" v-if="currentTicketNotes.length > 0">
                                    Timeline</h6>

                                <div class="notes-timeline overflow-auto pe-2" style="max-height: 350px;">
                                    <div v-for="note in currentTicketNotes" :key="note.id"
                                        class="card border border-light-subtle bg-white p-3 mb-3 shadow-sm rounded-3">
                                        <div
                                            class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom border-light">
                                            <span class="fw-bold text-dark small">
                                                <i class="bi bi-person-circle text-primary me-1"></i> {{ note.user?.name
                                                    || 'Usuário' }}
                                            </span>
                                            <span class="text-muted" style="font-size: 0.75rem;">
                                                <i class="bi bi-clock me-1"></i> {{ formatDateHour(note.created_at) }}
                                            </span>
                                        </div>
                                        <p class="mb-0 text-secondary small"
                                            style="white-space: pre-wrap; line-height: 1.5;">{{ note.content }}</p>
                                    </div>

                                    <div v-if="!currentTicketNotes || currentTicketNotes.length === 0"
                                        class="text-center text-muted small py-5 bg-light rounded-3 border border-dashed mt-3">
                                        <i class="bi bi-chat-square-dots fs-2 opacity-25 d-block mb-2"></i>
                                        Nenhuma interação registrada neste atendimento ainda.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light" data-bs-dismiss="modal">Fechar</button>
                        <button class="btn btn-pmg-primary px-4" @click="saveTicket"
                            :disabled="loading || !isFormValid">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span> Salvar
                            Atendimento
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Confirmação Deleção -->
        <div class="modal fade" id="confirmDeleteCrmModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">Excluir o ticket <strong>{{ itemToDelete?.protocol }}</strong>? Esta
                            ação é irreversível.</p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Modal Filtros de Relatórios do CRM -->
    <div class="modal fade" id="modalCrmReport" tabindex="-1" aria-hidden="true" ref="modalCrmReport">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4">
                <div class="modal-header border-0 pt-4 px-4 pb-2">
                    <h5 class="fw-bold m-0 text-dark">Filtros do Relatório de Atendimentos</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div class="modal-body p-4">
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Aberto a partir de</label>
                            <input type="date" v-model="reportFilters.start_date" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Aberto até</label>
                            <input type="date" v-model="reportFilters.end_date" class="form-control form-control-pmg">
                        </div>

                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Status do Atendimento</label>
                            <select v-model="reportFilters.status" class="form-select form-control-pmg">
                                <option value="">Todos os Status</option>
                                <option value="Aberto">Aberto</option>
                                <option value="Em Andamento">Em Andamento</option>
                                <option value="Aguardando Cliente">Aguardando Cliente</option>
                                <option value="Concluído">Concluído</option>
                            </select>
                        </div>

                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Motivo / Assunto</label>
                            <select v-model="reportFilters.reason_id" class="form-select form-control-pmg">
                                <option value="">Todos os Motivos</option>
                                <option v-for="r in reasons" :key="r.id" :value="r.id">{{ r.title }}</option>
                            </select>
                        </div>

                        <div class="col-md-12">
                            <label class="form-label small fw-bold text-muted">Protocolo (Busca parcial)</label>
                            <input type="text" v-model="reportFilters.protocol" class="form-control form-control-pmg"
                                placeholder="Ex: CRM-2026-...">
                        </div>
                    </div>

                    <hr class="my-4 text-muted">

                    <!-- NOVA SEÇÃO: Escolha de Campos (Inspirado no list-client.vue) -->
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="fw-bold text-dark m-0">Campos do Relatório (Mín. 2)</h6>

                        <div class="form-check form-switch m-0">
                            <input class="form-check-input cursor-pointer" type="checkbox" id="chk_selectAllFields"
                                v-model="selectAllFields">
                            <label class="form-check-label small fw-bold cursor-pointer" for="chk_selectAllFields">
                                Selecionar Todos
                            </label>
                        </div>
                    </div>

                    <div class="row g-2 mb-3">
                        <div class="col-md-4 col-sm-6" v-for="field in availableFields" :key="field.id">
                            <div class="form-check">
                                <input class="form-check-input cursor-pointer" type="checkbox" :value="field.id"
                                    :id="'chk_' + field.id" v-model="reportFilters.fields">
                                <label class="form-check-label small cursor-pointer" :for="'chk_' + field.id">
                                    {{ field.label }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <button class="btn btn-pmg-primary w-100 py-3 fw-bold"
                            @click="exportFormat === 'pdf' ? downloadCrmReportPdf() : exportCrmExcel()"
                            :disabled="loadingReport || !isReportValid">
                            <template v-if="loadingReport">
                                <span class="spinner-border spinner-border-sm me-2"></span>
                                PROCESSANDO...
                            </template>
                            <template v-else>
                                <i :class="exportFormat === 'pdf' ? 'bi-file-earmark-pdf-fill' : 'bi-file-earmark-excel-fill'"
                                    class="me-2"></i>
                                {{ exportFormat === 'pdf' ? 'GERAR RELATÓRIO PDF' : 'EXPORTAR PLANILHA XLSX' }}
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            tickets: [],
            reasons: [],
            entitiesList: [],
            itemsList: [],
            alerts: [],
            loading: false,
            loadingEntities: false,
            loadingItems: false,
            isEditing: false,
            showFilter: false,
            filter: { search: '', protocol: '', status: '', reason_id: '' },
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            hasMore: false,
            stats: { total: 0, open: 0, delayed: 0, completed: 0 },
            form: {
                id: null,
                crmable_type: 'App\\Models\\Client',
                crmable_id: '',
                reason_id: '',
                new_reason: '',
                itemable_type: '',
                itemable_id: '',
                status: 'Aberto',
                return_date: '',
                description: ''
            },
            modalInstance: null,
            deleteModalInstance: null,
            itemToDelete: null,
            contactSearchQuery: '',
            contactSearchResults: [],
            showContactDropdown: false,
            contactDebounce: null,
            itemSearchQuery: '',
            itemSearchResults: [],
            showItemDropdown: false,
            itemDebounce: null,
            viewMode: localStorage.getItem('crm_view_mode') || 'list',
            kanbanColumns: ['Aberto', 'Em Andamento', 'Aguardando Cliente', 'Concluído'],

            // Variáveis para Notas Internas
            newNoteContent: '',
            currentTicketNotes: [],
            isSavingNote: false,
            activeTab: 'details',
            loadingReport: false,
            reportModalInstance: null,
            exportFormat: 'pdf',
            reportFilters: {
                start_date: '',
                end_date: '',
                status: '',
                reason_id: '',
                protocol: '',
                // Inicia com colunas padrão selecionadas
                fields: ['protocol', 'created_at', 'client', 'reason', 'status', 'user']
            },

            // Definição dos campos disponíveis para o CRM
            availableFields: [
                { id: 'protocol', label: 'Protocolo' },
                { id: 'created_at', label: 'Data de Abertura' },
                { id: 'client', label: 'Cliente/Fornecedor' },
                { id: 'reason', label: 'Motivo/Assunto' },
                { id: 'itemable', label: 'Produto/Serviço' },
                { id: 'status', label: 'Status' },
                { id: 'return_date', label: 'Previsão de Retorno' },
                { id: 'user', label: 'Atendente' },
                { id: 'description', label: 'Descrição Inicial' }
            ]
        };
    },
    computed: {
        isFormValid() {
            const hasReason = this.form.reason_id === 'NEW' ? this.form.new_reason.trim() !== '' : this.form.reason_id !== '';
            return this.form.crmable_type && this.form.crmable_id && hasReason && this.form.status && this.form.description.trim() !== '';
        },
        groupedTickets() {
            const groups = { 'Aberto': [], 'Em Andamento': [], 'Aguardando Cliente': [], 'Concluído': [] };
            this.tickets.forEach(ticket => {
                if (groups[ticket.status]) {
                    groups[ticket.status].push(ticket);
                } else {
                    groups['Aberto'].push(ticket);
                }
            });
            return groups;
        },
        isReportValid() {
            // Exige pelo menos 2 campos selecionados para gerar o relatório
            return this.reportFilters.fields.length >= 2;
        },

        selectAllFields: {
            get() {
                return this.reportFilters.fields.length === this.availableFields.length;
            },
            set(value) {
                if (value) {
                    this.reportFilters.fields = this.availableFields.map(f => f.id);
                } else {
                    this.reportFilters.fields = [];
                }
            }
        }
    },
    watch: {
        viewMode(novoValor) {
            localStorage.setItem('crm_view_mode', novoValor);
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.modalInstance = new window.bootstrap.Modal(document.getElementById('modalCrmTicket'));
            this.deleteModalInstance = new window.bootstrap.Modal(document.getElementById('confirmDeleteCrmModal'));
        });
        this.fetchStats();
        this.fetchReasons();
        this.fetchTickets();
        this.fetchEntities();
    },
    methods: {
        async fetchStats() {
            try {
                const response = await api.get('/crm-tickets/stats');
                this.stats = response.data;
            } catch (e) {
                console.error("Erro ao carregar dashboard de tickets", e);
            }
        },
        async fetchTickets(page = 1, append = false) {
            this.globalLoading.show('Carregando dados...');
            this.loading = true;
            try {
                const res = await api.get('/crm-tickets', { params: { page, ...this.filter } });
                const responseData = res.data;
                this.tickets = append ? [...this.tickets, ...responseData.data] : responseData.data;
                this.totalItems = responseData.total;
                this.currentPage = responseData.current_page;
                this.lastPage = responseData.last_page;
                this.hasMore = this.currentPage < this.lastPage;
            } catch (e) {
                console.error(e);
                this.showAlert("Erro ao buscar atendimentos.", "error");
            } finally {
                this.globalLoading.hide();
                this.loading = false;
            }
        },

        async fetchReasons() {
            try {
                const res = await api.get('/crm-reasons');
                this.reasons = res.data.data || res.data;
            } catch (e) { console.error("Erro ao buscar motivos", e); }
        },

        async fetchEntities() {
            this.loadingEntities = true;
            this.form.crmable_id = '';
            try {
                const endpoint = this.form.crmable_type === 'App\\Models\\Client' ? '/clients' : '/suppliers';
                const res = await api.get(endpoint);
                this.entitiesList = res.data.data || res.data;
            } catch (e) {
                console.error("Erro ao buscar entidades", e);
            } finally {
                this.loadingEntities = false;
            }
        },

        async fetchItems() {
            if (!this.form.itemable_type) {
                this.itemsList = [];
                this.form.itemable_id = '';
                return;
            }
            this.loadingItems = true;
            this.form.itemable_id = '';
            try {
                const endpoint = this.form.itemable_type === 'App\\Models\\Product' ? '/products' : '/services';
                const res = await api.get(endpoint);
                this.itemsList = res.data.data || res.data;
            } catch (e) {
                console.error("Erro ao buscar itens", e);
            } finally {
                this.loadingItems = false;
            }
        },

        openModal(mode, item = null) {
            this.isEditing = mode === 'edit';
            this.activeTab = 'details';
            if (this.isEditing && item) {
                this.form = {
                    ...item,
                    new_reason: ''
                };
                this.contactSearchQuery = item.crmable?.name || '';
                this.itemSearchQuery = item.itemable?.name || '';

                // Carrega as notas do ticket e limpa a textarea de nova nota
                this.currentTicketNotes = item.notes || [];
                this.newNoteContent = '';
            } else {
                this.form = {
                    id: null,
                    crmable_type: 'App\\Models\\Client',
                    crmable_id: '',
                    reason_id: '',
                    new_reason: '',
                    itemable_type: '',
                    itemable_id: '',
                    status: 'Aberto',
                    return_date: '',
                    description: ''
                };
                this.contactSearchQuery = '';
                this.contactSearchResults = [];
                this.itemSearchQuery = '';
                this.itemSearchResults = [];

                // Limpa as variáveis de nota ao criar um novo
                this.currentTicketNotes = [];
                this.newNoteContent = '';
            }

            this.modalInstance.show();
        },

        async saveNote() {
            if (!this.newNoteContent.trim()) return;

            this.isSavingNote = true;
            try {
                const response = await api.post(`/crm-tickets/${this.form.id}/notes`, {
                    content: this.newNoteContent
                });

                // Joga a nota nova direto no topo da tela sem recarregar a página
                this.currentTicketNotes.unshift(response.data.note);

                // Limpa a caixa de texto
                this.newNoteContent = '';
                this.showAlert('Interação salva com sucesso!', 'success');
            } catch (e) {
                console.error("Erro ao salvar nota", e);
                this.showAlert('Erro ao registrar a nota interna.', 'error');
            } finally {
                this.isSavingNote = false;
            }
        },

        async saveTicket() {
            this.loading = true;
            try {
                const payload = { ...this.form };
                if (payload.reason_id !== 'NEW') {
                    delete payload.new_reason;
                } else {
                    payload.reason_id = null;
                }

                if (this.isEditing) {
                    await api.put(`/crm-tickets/${this.form.id}`, payload);
                } else {
                    await api.post('/crm-tickets', payload);
                }

                this.showAlert("Atendimento salvo com sucesso!");
                this.modalInstance.hide();
                this.fetchTickets(1, false);
                this.fetchStats();
            } catch (e) {
                this.showAlert("Erro ao salvar o atendimento.", "error");
            } finally {
                this.loading = false;
            }
        },

        confirmDelete(item) {
            this.itemToDelete = item;
            this.deleteModalInstance.show();
        },

        async executeDeletion() {
            try {
                await api.delete(`/crm-tickets/${this.itemToDelete.id}`);
                this.showAlert("Ticket excluído com sucesso!", "danger");
                this.fetchTickets(1, false);
            } catch (e) {
                this.showAlert("Erro ao excluir.", "error");
            } finally {
                this.deleteModalInstance.hide();
            }
        },

        applyFilter() {
            this.showFilter = false;
            this.fetchTickets(1, false);
        },

        loadMore() {
            if (this.hasMore) this.fetchTickets(this.currentPage + 1, true);
        },

        getEntityLabel(type) {
            if (type === 'App\\Models\\Client') return 'Cliente';
            if (type === 'App\\Models\\Supplier') return 'Fornecedor';
            return 'Outro';
        },

        getStatusBadgeClass(status) {
            const lower = status?.toLowerCase();
            if (lower === 'aberto') return 'badge bg-primary-subtle text-primary border border-primary-subtle';
            if (lower === 'concluído') return 'badge bg-success-subtle text-success border border-success-subtle';
            if (lower === 'aguardando cliente') return 'badge bg-warning-subtle text-warning border border-warning-subtle';
            return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },

        formatDateHour(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('pt-BR');
        },

        formatShortDate(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
            return d.toLocaleDateString('pt-BR');
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';
            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), 8000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },
        onCrmableTypeChange() {
            this.contactSearchQuery = '';
            this.form.crmable_id = '';
            this.contactSearchResults = [];
        },

        onContactInput() {
            this.showContactDropdown = true;
            clearTimeout(this.contactDebounce);

            if (!this.contactSearchQuery || this.contactSearchQuery.length < 2) {
                this.contactSearchResults = [];
                return;
            }

            this.contactDebounce = setTimeout(async () => {
                try {
                    const endpoint = this.form.crmable_type === 'App\\Models\\Client' ? '/clients' : '/suppliers';
                    const res = await api.get(endpoint, { params: { search: this.contactSearchQuery, limit: 8 } });
                    this.contactSearchResults = res.data.data || res.data;
                } catch (e) {
                    console.error("Erro ao buscar contatos", e);
                }
            }, 300);
        },

        selectContact(contact) {
            this.form.crmable_id = contact.id;
            this.contactSearchQuery = contact.name;
            this.showContactDropdown = false;
        },

        hideContactDropdown() {
            setTimeout(() => {
                this.showContactDropdown = false;
            }, 200);
        },
        onItemableTypeChange() {
            this.itemSearchQuery = '';
            this.form.itemable_id = '';
            this.itemSearchResults = [];
        },

        onItemInput() {
            this.showItemDropdown = true;
            clearTimeout(this.itemDebounce);

            if (!this.itemSearchQuery || this.itemSearchQuery.length < 2) {
                this.itemSearchResults = [];
                return;
            }

            this.itemDebounce = setTimeout(async () => {
                try {
                    const endpoint = this.form.itemable_type === 'App\\Models\\Product' ? '/products' : '/services';
                    const res = await api.get(endpoint, { params: { search: this.itemSearchQuery, limit: 8 } });
                    this.itemSearchResults = res.data.data || res.data;
                } catch (e) {
                    console.error("Erro ao buscar itens", e);
                }
            }, 300);
        },

        selectItem(item) {
            this.form.itemable_id = item.id;
            this.itemSearchQuery = item.name ?? item.description ?? '';
            this.showItemDropdown = false;
        },

        hideItemDropdown() {
            setTimeout(() => {
                this.showItemDropdown = false;
            }, 200);
        },
        formatMoney(value) {
            return parseFloat(value || 0).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        onDragStart(event, ticket) {
            event.dataTransfer.dropEffect = 'move';
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('ticketId', ticket.id);
        },

        onDrop(event, newStatus) {
            const ticketId = event.dataTransfer.getData('ticketId');
            const ticket = this.tickets.find(t => t.id == ticketId);

            if (ticket && ticket.status !== newStatus) {
                const oldStatus = ticket.status;
                ticket.status = newStatus;
                this.updateTicketStatus(ticket, newStatus, oldStatus);
            }
        },
        async updateTicketStatus(ticket, newStatus, oldStatus) {
            try {
                await api.put(`/crm-tickets/${ticket.id}`, {
                    ...ticket,
                    status: newStatus
                });

                this.fetchStats();
                this.showAlert(`Status alterado para ${newStatus}!`, 'success');
            } catch (e) {
                ticket.status = oldStatus;
                this.showAlert('Erro ao alterar status no servidor.', 'error');
            }
        },

        formatDateBR(dateStr) {
            if (!dateStr) return '';
            const parts = dateStr.split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
            return dateStr;
        },

        isDelayed(dateStr) {
            if (!dateStr) return false;
            const ticketDate = new Date(dateStr + 'T00:00:00');
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return ticketDate < today;
        },

        getCardColor(status) {
            const colors = {
                'Aberto': 'border-start border-4 border-dark bg-secondary-subtle',
                'Em Andamento': 'border-start border-4 border-primary bg-primary-subtle',
                'Aguardando Cliente': 'border-start border-4 border-warning bg-warning-subtle',
                'Concluído': 'border-start border-4 border-success bg-success-subtle'
            };

            return colors[status] || 'border-start border-4 border-secondary bg-light';
        },
        openReportModal(format = 'pdf') {
            this.exportFormat = format;
            if (!this.reportModalInstance) {
                const reportEl = document.getElementById('modalCrmReport');
                if (reportEl) {
                    this.reportModalInstance = new window.bootstrap.Modal(reportEl);
                }
            }
            if (this.reportModalInstance) {
                this.reportModalInstance.show();
            }
        },

        async downloadCrmReportPdf() {
            this.loadingReport = true;
            if (this.globalLoading) this.globalLoading.show('Gerando PDF...');
            try {
                // Clona os filtros e transforma o array de campos em string separada por vírgula
                const filtersToSend = { ...this.reportFilters };
                filtersToSend.fields = filtersToSend.fields.join(',');

                const params = new URLSearchParams(filtersToSend).toString();
                const response = await api.get(`/crm-tickets/report/pdf?${params}`, {
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `relatorio_crm_${Date.now()}.pdf`);
                document.body.appendChild(link);
                link.click();

                window.URL.revokeObjectURL(url);
                link.remove();

                if (this.reportModalInstance) this.reportModalInstance.hide();
                this.showAlert("Relatório gerado com sucesso!");
            } catch (e) {
                console.error("Erro ao gerar PDF do CRM:", e);
                this.showAlert("Erro ao processar o arquivo PDF.", "error");
            } finally {
                this.loadingReport = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        },

        async exportCrmExcel() {
            this.loadingReport = true;
            if (this.globalLoading) this.globalLoading.show('Gerando Planilha...');
            try {
                // Mesma lógica de tratamento dos campos para o Excel
                const filtersToSend = { ...this.reportFilters };
                filtersToSend.fields = filtersToSend.fields.join(',');

                const params = new URLSearchParams(filtersToSend).toString();
                const response = await api.get(`/crm-tickets/report/excel?${params}`, {
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `planilha_crm_${Date.now()}.xlsx`);
                document.body.appendChild(link);
                link.click();

                window.URL.revokeObjectURL(url);
                link.remove();

                if (this.reportModalInstance) this.reportModalInstance.hide();
                this.showAlert("Planilha gerada com sucesso!");
            } catch (e) {
                console.error("Erro ao exportar planilha Excel:", e);
                this.showAlert("Erro ao exportar planilha.", "error");
            } finally {
                this.loadingReport = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        }
    }
}
</script>

<style scoped>
/* Copiando exatamente as classes padrão da sua arquitetura RMR */
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary,
.btn-pmg-light,
.btn-pmg-danger {
    padding: 10px 24px !important;
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    height: 45px;
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
}

.btn-pmg-primary:hover {
    background: #1a294d;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 51, 97, 0.2);
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
}

.btn-pmg-light:hover {
    background-color: #f8fafc;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
}

.form-control-pmg {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 10px 14px;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.tiny {
    font-size: 0.7rem;
}

.bg-pmg-light {
    background-color: rgba(13, 110, 253, 0.1);
}

.bg-success-light {
    background-color: rgba(25, 135, 84, 0.1);
}

.bg-danger-light {
    background-color: rgba(220, 53, 69, 0.1);
}

.bg-warning-light {
    background-color: rgba(255, 193, 7, 0.1);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.dropdown-menu {
    z-index: 1050 !important;
}

.table-responsive {
    overflow: visible !important;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}

.cursor-grab {
    cursor: grab;
}

.cursor-grab:active {
    cursor: grabbing;
}

.kanban-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kanban-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
}

.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Estilização Customizada das Abas do Modal */
.nav-tabs .nav-link {
    color: #64748b;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 10px 20px;
    transition: all 0.2s ease;
}

.nav-tabs .nav-link:hover {
    border-color: transparent;
    color: #1e293b;
    background-color: #f8fafc;
    border-radius: 8px 8px 0 0;
}

.nav-tabs .nav-link.active {
    color: #561569;
    /* Cor Primária RMR */
    border-bottom: 2px solid #561569;
    background: transparent;
}

.kanban-scroll-area {
    /* O height força a coluna a ter o tamanho da tela MENOS o topo inteiro (420px) */
    height: calc(100vh - 420px) !important;
    min-height: 300px;
    overflow-y: auto !important;
    overflow-x: hidden;
    padding-right: 8px;
}

/* Customização minimalista da barra de rolagem */
.kanban-scroll-area::-webkit-scrollbar {
    width: 6px;
}

.kanban-scroll-area::-webkit-scrollbar-track {
    background: transparent;
}

.kanban-scroll-area::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 8px;
}

.kanban-scroll-area::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
}
</style>